/**
 * 메일 상세의 대용량 첨부 카드.
 *
 * 왜 별도 카드인가
 *   대용량은 EML 안에 없고 본문 '링크' 로 나간다. 그래서 목록의 클립 아이콘 개수에도 안 잡히고
 *   (수신자 메일 클라이언트도 마찬가지다), 일반 첨부 목록에도 섞이지 않는다.
 *   여기서 따로 보여주지 않으면 발신자가 "4개 붙였는데 왜 2개지" 로 헷갈린다.
 *
 * 일반 첨부와 다른 정보
 *   남은 일수 / 다운로드 횟수 / 회수 — 전부 "공개 링크" 라는 성격에서 나온다.
 */
import React, { useEffect, useState } from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { formatFileSize as formatBytes } from "../../../util/numberFormat";
import { notify } from "../../../util/toast";
import { confirm } from "../../../util/confirmDialog";
import { mailApi } from "../api/mailApi";

/** 상태별 배지 — 수신자가 보게 될 결과와 같은 어휘를 쓴다. */
const STATUS_CHIP = {
    UPLOADED:  null,                                        // 정상 — 배지 없이 남은 일수만
    EXPIRED:   { label: "만료됨",   color: "#64748b", bg: "#f1f5f9" },
    REVOKED:   { label: "회수됨",   color: "#b45309", bg: "#fef3c7" },
    ABANDONED: { label: "정리됨",   color: "#64748b", bg: "#f1f5f9" },
    PENDING:   { label: "업로드 중", color: "#1d4ed8", bg: "#dbeafe" },
};

export default function LargeAttachmentCard({ items: initialItems, canRevoke = false }) {
    const [busyId, setBusyId] = useState(null);
    /*
      회수 결과를 로컬에서 반영한다. 부모(MailPage)까지 재조회를 올리면 목록·본문이 통째로 다시
      그려지는데, 바뀌는 건 이 카드의 배지 하나뿐이라 비용이 맞지 않는다.
      서버는 이미 status=REVOKED 로 확정했으므로 화면만 맞추면 된다.
    */
    const [items, setItems] = useState(initialItems || []);
    useEffect(() => { setItems(initialItems || []); }, [initialItems]);

    if (!items?.length) return null;

    const total = items.reduce((s, it) => s + (it.sizeBytes || 0), 0);

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            notify("링크를 복사했습니다.", "success");
        } catch {
            notify("링크 복사에 실패했습니다. 본문의 링크를 직접 복사해 주세요.", "error");
        }
    };

    const revoke = async (it) => {
        const ok = await confirm({
            title: "대용량 첨부 회수",
            message: `"${it.filename}" 을(를) 회수할까요?\n`
                   + "파일이 즉시 삭제되고, 받는 사람이 링크를 눌러도 받을 수 없게 됩니다.\n"
                   + "되돌릴 수 없습니다.",
            confirmLabel: "회수",
            variant: "danger",
        });
        if (!ok) return;
        setBusyId(it.id);
        try {
            await mailApi.largeRevoke(it.id);
            notify("회수했습니다. 받는 사람은 더 이상 파일을 받을 수 없습니다.", "success");
            setItems(prev => prev.map(x =>
                x.id === it.id ? { ...x, status: "REVOKED", publicUrl: null, daysLeft: null } : x));
        } catch (e) {
            notify(e?.response?.data?.message || "회수에 실패했습니다.", "error");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Box sx={{ mb: 2, border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{ px: 1.75, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
                    대용량 첨부 {items.length}개 · {formatBytes(total)}
                </Typography>
            </Box>

            {items.map((it, idx) => {
                const chip = STATUS_CHIP[it.status];
                const alive = it.status === "UPLOADED";
                return (
                    <Box key={it.id} sx={{
                        display: "flex", alignItems: "center", gap: 1.25,
                        px: 1.75, py: 1.1,
                        borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                    }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: 13, color: alive ? "#1e293b" : "#94a3b8",
                                textDecoration: alive ? "none" : "line-through",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {it.filename}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} alignItems="center">
                                <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                    {formatBytes(it.sizeBytes)}
                                </Typography>
                                {alive && it.daysLeft != null && (
                                    <Typography sx={{ fontSize: 11.5, color: it.daysLeft <= 3 ? "#b45309" : "#64748b" }}>
                                        · {it.daysLeft === 0 ? "오늘 만료" : `${it.daysLeft}일 남음`}
                                    </Typography>
                                )}
                                {/* 다운로드 횟수 — 링크가 전달·유출됐는지 짐작할 수 있는 유일한 단서다. */}
                                <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                    · {it.downloadCount || 0}회 다운로드
                                </Typography>
                            </Stack>
                        </Box>

                        {chip && (
                            <Chip label={chip.label} size="small" sx={{
                                height: 20, fontSize: 10.5, fontWeight: 700,
                                bgcolor: chip.bg, color: chip.color, flexShrink: 0,
                            }} />
                        )}

                        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                            {alive && it.publicUrl && (
                                <Tooltip title="다운로드 링크 복사">
                                    <IconButton size="small" onClick={() => copyLink(it.publicUrl)}>
                                        <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {/* 회수는 발신자만. 서버도 소유권을 검사하지만 남에게 버튼을 보일 이유가 없다. */}
                            {canRevoke && alive && (
                                <Tooltip title="회수 — 파일을 즉시 삭제하고 링크를 막습니다">
                                    <span>
                                        <IconButton size="small" disabled={busyId === it.id}
                                                    onClick={() => revoke(it)}>
                                            <BlockRoundedIcon sx={{ fontSize: 15, color: "#b45309" }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    </Box>
                );
            })}

            <Box sx={{ px: 1.75, py: 0.75, bgcolor: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                    본문의 링크로 다운로드합니다. 링크를 가진 누구나 받을 수 있으며, 기간이 지나면 자동 삭제됩니다.
                </Typography>
            </Box>
        </Box>
    );
}
